// web-app/src/components/2d/runPointerTargets.js
//
// What a POINTER landing on the plan should claim, of the cabinet-run
// affordances — the two questions the mouse path and the touch path must
// answer the same way.
//
// They did not. `handleMouseDown` asks `runDrag` about the leg's Move disc
// and the run's end handles; `touchDragTarget` (the touch path's own
// hit-test, which decides "claim this finger" vs "pan the plan") never knew
// cabinet runs existed. So a finger on a visible disc found nothing to claim
// it and panned the whole canvas, while eight INVISIBLE physical resize
// handles around the selected cabinet — neither drawn nor honoured for a
// run-owned object — claimed it for a drag the mouse path then declined.
//
// Pure: no React, no canvas, no events. `runDrag` is the object
// `useRunDrag` returns (or null).

/**
 * Is the plan's finger-sized GRAB handle offered for `selected`?
 *
 * A run-owned object gets it only when it is a bay the run lets drag ("Move
 * along run"): the bay drag projects the pointer onto the wall, so a handle
 * offset ACROSS the wall does not move the appliance along it. The renderer
 * and the touch hit-test share this predicate so a finger can never land on
 * a handle that was not drawn.
 *
 * @param {Object|null} selected
 * @param {Object|null} runDrag
 * @returns {boolean}
 */
export function grabHandleOfferedFor(selected, runDrag) {
  if (!selected) return false;
  if (!selected.runId) return true;
  return !!runDrag?.canDragBay?.(selected);
}

/**
 * Is the selected object's MOVE handle offered — the blue disc beside it that
 * drags it along its run, with no "Move along run" in front of it (user
 * request 2026-10-26)? A press on the handle is unambiguous; only a press on
 * the object's BODY could equally mean pan or select, so that one still waits
 * for the mode.
 *
 * Offered for what can slide along a run — a bay's appliance or sink base
 * (`canDragBay`), and a plain run cabinet, which is put down where it is
 * dropped (`canPlaceCabinet`) — in the BAY scope: the run scope has the run's
 * own handles, and an island's piece is moved by the leg's disc there, which
 * is what the body drag does with it too (`canDragLeg` first).
 *
 * @param {Object|null} selected
 * @param {Object|null} runDrag
 * @param {'bay'|'run'} [scope]
 * @returns {boolean}
 */
export function moveHandleOfferedFor(selected, runDrag, scope = 'bay') {
  if (!selected || !runDrag || scope === 'run') return false;
  if (runDrag.canDragLeg?.(selected)) return false;
  return (
    !!runDrag.canDragBay?.(selected) || !!runDrag.canPlaceCabinet?.(selected)
  );
}

/**
 * What the run affordances claim at `point`.
 *
 * @returns {'handle'|null|undefined}
 *   `'handle'` — a run handle is under the pointer; start a run drag.
 *                Which handles exist depends on `scope` — see below.
 *   `null`     — `selected` is run-owned, none of ITS handles are under the
 *                pointer, and the generic hit-tests are armed and would answer
 *                wrongly: pan.
 *   `undefined` — not a run question; carry on with the generic hit-tests.
 */
export function runPointerTarget({
  point,
  selected,
  scale,
  coarse = false,
  runDrag = null,
  mode,
  scope = 'bay',
  // Does `point` land on the selected object's move handle? Asked of the
  // caller, which knows the room it faces (`grabHandleUtils`).
  hitMoveHandle = null,
}) {
  if (!runDrag || !selected || !point) return undefined;

  // THE HANDLES, with nothing armed in front of them (2026-10-03): grabbing one
  // is an unambiguous gesture, and only a drag on an object's BODY needs a mode
  // because that one could equally mean pan or select.
  //
  // What they DO follow is the SCOPE — which of the bay and the run the open
  // panel is editing, and the one the canvas paints pink. A handle for the
  // other scope would contradict the highlight, and this must agree with the
  // renderer exactly or a finger lands on a handle that was never drawn.
  if (scope === 'run') {
    // The leg's MOVE disc first: it is the only affordance that says where to
    // grab a 13-foot island, and it sits at the leg's midpoint — nowhere near
    // whichever cabinet of it happens to be selected.
    if (runDrag.hitTestMoveHandle?.(point, selected, scale, { coarse })) {
      return 'handle';
    }
    // Then the run's END handles. A PINNED end claims the pointer too: the
    // mouse path answers it with a toast naming what stopped it, and a pan
    // would swallow that.
    if (
      runDrag.runIdFor?.(selected) &&
      runDrag.hitTestEnd(point, selected, scale, { coarse })
    ) {
      return 'handle';
    }
    // …and its CORNERS — a held one claims the pointer too, for the same
    // reason: the press answers with why it cannot move.
    if (
      runDrag.runIdFor?.(selected) &&
      runDrag.hitTestCorner?.(point, selected, scale, { coarse })
    ) {
      return 'handle';
    }
  } else if (runDrag.hitTestBayEdge?.(point, selected, scale, { coarse })) {
    // A BAY's own edge, in the scope that paints that bay pink.
    return 'handle';
  } else if (
    moveHandleOfferedFor(selected, runDrag, scope) &&
    hitMoveHandle?.(point)
  ) {
    // …and its MOVE handle, which drags the whole bay along the run.
    return 'handle';
  }

  // No run handle under the pointer. In RESIZE it must not fall through: a
  // run-owned object's own eight handles are not drawn (the box is refilled
  // from the spec, it does not resize), so claiming the pointer for them would
  // only produce a drag that dies — and swallowing the press instead would
  // leave the plan un-pannable while a run cabinet is selected. That rule is
  // still about the GENERIC handles, which are still mode-armed, so it keeps
  // its mode. In the other modes the generic path has real work left — a bay's
  // body drag, the coarse grab handle — so it carries on.
  if (mode === 'resize' && runDrag.runIdFor?.(selected)) return null;

  return undefined;
}
